// src/pages/Dashboard/Admin/DetailsAdherents/components/AdherentHeroCard.jsx

import React from "react";
import { FaEnvelope, FaPhone } from "react-icons/fa";

import {
  computeAge,
  formatDate,
  getAdherentStatusClass,
  getFullName,
  getGroupsFromAdherent,
  toAbsoluteUrl,
} from "../utils/adherentDashboardUtils";

function Badge({ children, className }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ring-1 ${className}`}
    >
      {children}
    </span>
  );
}

export default function AdherentHeroCard({
  adherent,
  ageCategory,
  weightCategory,
}) {
  const groups = getGroupsFromAdherent(adherent);

  const photoUrl =
    toAbsoluteUrl(adherent?.photo_url || adherent?.photoURL) ||
    "/img/user_avatar.jpg";

  const age = computeAge(adherent?.date_naissance);

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <img
              src={photoUrl}
              alt={getFullName(adherent)}
              className="h-28 w-28 rounded-[26px] object-cover ring-1 ring-slate-200 dark:ring-slate-700"
              onError={(event) => {
                event.currentTarget.src = "/img/user_avatar.jpg";
              }}
            />

            <div className="min-w-0">
              <div className="flex flex-wrap gap-2">
                <Badge className={getAdherentStatusClass(adherent?.statut_inscription)}>
                  {adherent?.statut_inscription || "Statut non renseigné"}
                </Badge>

                {adherent?.saison_active ? (
                  <Badge className="bg-indigo-50 text-indigo-700 ring-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20">
                    Saison {adherent.saison_active}
                  </Badge>
                ) : null}
              </div>

              <h1 className="mt-3 text-2xl font-semibold text-slate-900 sm:text-3xl dark:text-white">
                {(adherent?.nom || "").toUpperCase()} {adherent?.prenom || ""}
              </h1>

              <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
                {adherent?.date_naissance
                  ? `Né(e) le ${formatDate(adherent.date_naissance)}${
                      age !== null ? ` · ${age} ans` : ""
                    }`
                  : "Date de naissance non renseignée"}
                {adherent?.commune ? ` · ${adherent.commune}` : ""}
              </p>

              <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
                Catégorie âge : <span className="font-bold">{ageCategory}</span>{" "}
                · Catégorie poids :{" "}
                <span className="font-bold">{weightCategory}</span>
              </p>

              <div className="mt-3 flex flex-wrap gap-2">
                {groups.length ? (
                  groups.map((group) => (
                    <span
                      key={group.id || group.nom}
                      className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                    >
                      {group.nom}
                    </span>
                  ))
                ) : (
                  <span className="rounded-full bg-rose-50 px-3 py-1 text-xs font-bold text-rose-700 dark:bg-rose-500/10 dark:text-rose-300">
                    Aucun groupe
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {adherent?.tel ? (
              <a
                href={`tel:${adherent.tel}`}
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaPhone />
                Appeler
              </a>
            ) : null}

            {adherent?.email ? (
              <a
                href={`mailto:${adherent.email}`}
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaEnvelope />
                Email
              </a>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}